import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Trophy, Clock, BarChart2, Eye } from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const InspectionTimeMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="inspection-time" // IMPORTANT: Change this for each game!
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "inspection-time", name: "Inspection Time" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={InspectionTimeMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const InspectionTimeMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [difficulty, setDifficulty] = useState("standard");
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Countdown states
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Feedback Overlay
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Basic game state
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(20);

  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);

  // Reaction time tracking
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [averageResponseTime, setAverageResponseTime] = useState(0);

  // Score
  const [score, setScore] = useState(0);

  // Stimulus display
  const [showStimuli, setShowStimuli] = useState(false);

  // Shapes array for each round
  const [shapes, setShapes] = useState([]); // array of shape objects
  const [correctIndex, setCorrectIndex] = useState(null); // which shape is largest?

  // Refs for game active state and timers
  const gameActive = useRef(false);
  const stimulusTimerRef = useRef(null);

  // Difficulty settings: how long shapes remain visible, total time, etc.
  const difficultySettings = {
    easy: {
      timeLimit: 60,
      inspectionTime: 1000,
    },
    standard: {
      timeLimit: 60,
      inspectionTime: 600,
    },
    advanced: {
      timeLimit: 60,
      inspectionTime: 300,
    },
  };

  // Colors for the game
  const textAccent = "text-red-300";

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // ----------------------------------
  //        GAME & TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (stimulusTimerRef.current) clearTimeout(stimulusTimerRef.current);
    };
  }, [isPlaying]);

  // Start the game with countdown
  const startGame = () => {
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setCurrentRound(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setResponseTimes([]);
    setAverageResponseTime(0);

    setTimeLeft(difficultySettings[difficulty].timeLimit);
    setIsPlaying(true);
    gameActive.current = true;

    // Get difficulty from gameState if available
    if (gameState && gameState.gameDifficulty) {
      setDifficulty(gameState.gameDifficulty);
    }

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }

    startNewRound();
  };

  /*
    generateShapes(difficulty):
    - For 'easy', we'll do 2 "lines" with random sizes
    - For 'standard', do 2 shapes (circle, square, etc.)
    - For 'advanced', do 3 shapes
  */
  const generateShapes = (diff) => {
    if (diff === "easy") {
      // 2 lines
      const sizes = [
        100 + Math.floor(Math.random() * 40),
        100 + Math.floor(Math.random() * 40),
      ];
      // We only track type = 'line' here
      return sizes.map((sz) => ({ type: "line", size: sz }));
    } else if (diff === "standard") {
      // 2 shapes, let's randomize type for variety
      // Possible types: circle, square
      const shapeTypes = ["circle", "square"];
      const shapesArr = [0, 1].map(() => ({
        type: shapeTypes[Math.floor(Math.random() * shapeTypes.length)],
        size: 50 + Math.floor(Math.random() * 60), // random radius or side
      }));
      return shapesArr;
    } else {
      // 'advanced': 3 shapes
      // Let's pick from circle, square, triangle, etc. for variety
      const shapeTypes = ["circle", "square", "triangle"];
      const shapesArr = [0, 1, 2].map(() => ({
        type: shapeTypes[Math.floor(Math.random() * shapeTypes.length)],
        size: 40 + Math.floor(Math.random() * 60),
      }));
      return shapesArr;
    }
  };

  // Start a new round
  const startNewRound = () => {
    if (!gameActive.current || currentRound >= totalRounds) {
      endGame();
      return;
    }

    // generate shapes for this round
    const newShapes = generateShapes(difficulty);
    setShapes(newShapes);

    // find index of largest shape by .size
    let maxIndex = 0;
    for (let i = 1; i < newShapes.length; i++) {
      if (newShapes[i].size > newShapes[maxIndex].size) {
        maxIndex = i;
      }
    }
    setCorrectIndex(maxIndex);

    setShowStimuli(true);
    setCurrentRound((prev) => prev + 1);

    setResponseStartTime(Date.now());

    // hide shapes after inspectionTime
    const inspect = difficultySettings[difficulty].inspectionTime;
    stimulusTimerRef.current = setTimeout(() => {
      setShowStimuli(false);
    }, inspect);
  };

  // Handle user guess
  const handleGuess = (guessIndex) => {
    if (!isPlaying) return;

    const rt = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, rt]);

    const isCorrect = guessIndex === correctIndex;
    if (isCorrect) {
      setCorrectResponses((prev) => prev + 1);

      // Scoring approach
      let basePoints = 15; // each correct
      let speedBonus = 1; // default minimal bonus

      // Tighter breakpoints
      if (rt < 250) {
        speedBonus = 10;
      } else if (rt < 350) {
        speedBonus = 8;
      } else if (rt < 500) {
        speedBonus = 6;
      } else if (rt < 1000) {
        speedBonus = 4;
      } else if (rt < 1500) {
        speedBonus = 2;
      } else {
        speedBonus = 1;
      }

      const roundScore = basePoints + speedBonus;
      setScore((prev) => prev + roundScore);

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: currentRound / totalRounds,
          myScore: score + roundScore,
          myLastResponse: {
            correct: true,
            points: roundScore,
            responseTime: rt,
          },
        });
      }

      setFeedbackCorrect(true);
      setFeedbackMessage("Correct!");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 600);
    } else {
      setIncorrectResponses((prev) => prev + 1);
      // small penalty
      const penalty = 5;
      setScore((prev) => (prev > 0 ? prev - penalty : 0));

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: currentRound / totalRounds,
          myScore: score > 0 ? score - penalty : 0,
          myLastResponse: {
            correct: false,
            points: -penalty,
            responseTime: rt,
          },
        });
      }

      setFeedbackCorrect(false);
      setFeedbackMessage("Incorrect");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 600);
    }

    if (currentRound < totalRounds) {
      setTimeout(() => {
        startNewRound();
      }, 600);
    } else {
      setTimeout(() => {
        endGame();
      }, 600);
    }
  };

  // End the game
  const endGame = () => {
    if (scoreSubmitted) return;
    setScoreSubmitted(true);

    // Clean up timers
    if (timerRef.current) clearInterval(timerRef.current);
    if (stimulusTimerRef.current) clearTimeout(stimulusTimerRef.current);

    setIsPlaying(false);
    gameActive.current = false;
    setGameMode("results");

    // Calculate average response time
    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        correctResponses,
        incorrectResponses,
        averageResponseTime:
          averageResponseTime ||
          (responseTimes.length > 0
            ? Math.round(
                responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
              )
            : 0),
        accuracy:
          correctResponses + incorrectResponses > 0
            ? correctResponses / (correctResponses + incorrectResponses)
            : 0,
        difficulty,
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  useEffect(() => {
    // Check if the opponent has quit
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // Helper to render each shape in the UI
  // We only do something special if showStimuli=true
  const renderShape = (shapeObj, index) => {
    if (!showStimuli) {
      // if stimuli are hidden, show nothing (or a placeholder)
      return (
        <div
          key={index}
          style={{ width: 50, height: 50 }}
          className="bg-slate-600 rounded flex items-center justify-center"
        >
          ?
        </div>
      );
    }

    switch (shapeObj.type) {
      case "line":
        return (
          <div
            key={index}
            style={{
              width: "6px",
              height: `${shapeObj.size}px`,
              backgroundColor: "rgba(248,113,113,0.7)", // tailwind red-400/70
              borderRadius: "3px",
              transition: "height 0.2s",
            }}
          />
        );
      case "circle":
        return (
          <div
            key={index}
            style={{
              width: `${shapeObj.size}px`,
              height: `${shapeObj.size}px`,
              backgroundColor: "rgba(248,113,113,0.7)",
              borderRadius: "9999px",
              transition: "all 0.2s",
            }}
          />
        );
      case "square":
        return (
          <div
            key={index}
            style={{
              width: `${shapeObj.size}px`,
              height: `${shapeObj.size}px`,
              backgroundColor: "rgba(248,113,113,0.7)",
              borderRadius: "4px",
              transition: "all 0.2s",
            }}
          />
        );
      case "triangle":
        // quick CSS triangle: width=0, height=0, use border
        return (
          <div
            key={index}
            style={{
              width: 0,
              height: 0,
              borderLeft: `${shapeObj.size}px solid transparent`,
              borderRight: `${shapeObj.size}px solid transparent`,
              borderBottom: `${
                shapeObj.size * 1.5
              }px solid rgba(248,113,113,0.7)`,
              transition: "all 0.2s",
            }}
          />
        );
      default:
        return (
          <div
            key={index}
            style={{
              width: 50,
              height: 50,
              backgroundColor: "rgba(248,113,113,0.7)",
            }}
          />
        );
    }
  };

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className={`font-medium flex items-center gap-2 ${textAccent}`}>
            <Eye size={18} className="text-red-400" />
            <span>Inspection Time Challenge</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {/* Start button or waiting indicator */}
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  {waitingForOpponent ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 border-4 border-t-transparent border-red-500 rounded-full animate-spin"></div>
                      <p className="text-white text-xl">
                        Waiting for opponent...
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleReadyClick}
                      className="py-3 px-6 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg"
                    >
                      Ready to Play
                    </button>
                  )}
                </div>
              )}

              {/* Countdown overlay */}
              {showCountdown && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <div className="flex flex-col items-center">
                    <div className="text-6xl font-bold text-white mb-4">
                      {countdown > 0 ? countdown : "GO!"}
                    </div>
                    <div className="text-xl text-slate-300">Get ready...</div>
                  </div>
                </div>
              )}

              {/* Game stats header */}
              <div className="flex justify-between items-center mb-6">
                <div className="bg-red-500/20 px-4 py-2 rounded-lg">
                  <span className="text-red-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="ml-4 bg-red-500/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Opponent progress */}
              {gameState && opponent && (
                <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-300">
                      {opponent.name}'s Progress
                    </span>
                    <span className="text-xs text-white">
                      {gameState?.progress && gameState.progress[opponent.email]
                        ? `${Math.round(
                            gameState.progress[opponent.email] * 100
                          )}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="bg-red-500 h-full rounded-full"
                      style={{
                        width: `${
                          ((gameState?.progress &&
                            gameState.progress[opponent.email]) ||
                            0) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  {gameState?.scores &&
                    gameState.scores[opponent.email] !== undefined && (
                      <div className="mt-1 text-right text-xs text-red-300">
                        Score: {gameState.scores[opponent.email]}
                      </div>
                    )}
                </div>
              )}

              {/* Game content */}
              <div className="flex flex-col items-center mb-4">
                <h3 className="text-white font-medium mb-2">
                  {difficulty === "easy"
                    ? "Which line was longer?"
                    : "Which shape was biggest?"}
                </h3>
              </div>

              {/* Container for shapes */}
              <div
                className="flex items-center justify-center gap-8 mb-8 flex-wrap"
                style={{ minHeight: "180px" }}
              >
                {shapes.map((shape, idx) => (
                  <div key={idx}>{renderShape(shape, idx)}</div>
                ))}
              </div>

              {/* Guess Buttons */}
              <div className="flex gap-4 justify-center mb-8">
                {shapes.map((_, idx) => (
                  <button
                    key={`guess-${idx}`}
                    onClick={() => handleGuess(idx)}
                    disabled={!isPlaying || showFeedback}
                    className={`py-3 px-6 rounded-lg transition-colors ${
                      !isPlaying || showFeedback
                        ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                        : "bg-slate-800 hover:bg-slate-700 text-slate-100"
                    }`}
                  >
                    {difficulty === "easy"
                      ? idx === 0
                        ? "Left"
                        : "Right"
                      : `Choice ${idx + 1}`}
                  </button>
                ))}
              </div>

              {/* Feedback Overlay */}
              <AnimatePresence>
                {showFeedback && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10"
                  >
                    <div className="text-center">
                      <div
                        className={`text-3xl font-bold ${
                          feedbackCorrect ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {feedbackMessage}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Stats */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Round</div>
                  <div className="text-white font-medium">
                    {currentRound}/{totalRounds}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Correct</div>
                  <div className="text-green-400 font-medium">
                    {correctResponses}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {incorrectResponses}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">
                    Avg. Time (ms)
                  </div>
                  <div className="text-white font-medium">
                    {responseTimes.length > 0
                      ? Math.round(
                          responseTimes.reduce((a, b) => a + b, 0) /
                            responseTimes.length
                        )
                      : "N/A"}
                  </div>
                </div>
              </div>

              {/* Instructions reminder */}
              <div className="mt-6 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 max-w-md mx-auto text-center">
                Watch closely! Shapes appear briefly, then you must select which
                one was largest.
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-orange-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-red-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-red-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Explanation box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-red-300">
                    Inspection Time
                  </span>
                  : Measures how quickly you can process visual information.
                  <br />
                  <span className="font-medium text-red-300">
                    Response Speed
                  </span>
                  : Faster responses with high accuracy indicate stronger visual
                  processing.
                  <br />
                  <span className="font-medium text-red-300">
                    Lower Response Time
                  </span>
                  : Elite performers often respond accurately in under 300ms.
                </p>
              </div>

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-red-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-red-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Errors</span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Total Trials</span>
                      <span className="text-white">{currentRound}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-red-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Accuracy bar */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-orange-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300">
                      <span>Average Response Time</span>
                      <span>
                        {responseTimes.length > 0
                          ? `${Math.round(
                              responseTimes.reduce((a, b) => a + b, 0) /
                                responseTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300 mb-2">
                      <span>Fastest Response</span>
                      <span>
                        {responseTimes.length > 0
                          ? `${Math.min(...responseTimes)} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Lower response times with high accuracy indicate
                        stronger visual processing abilities. Keep practicing to
                        improve both speed and accuracy!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionTimeMultiWrapper;
